"use client";

import clsx from "clsx";
import Link from "next/link";
import { usePathname } from "next/navigation";

const NAV_ITEMS = [
  { url: "/about-us", label: "About Us" },
  { url: "/charter", label: "Charter" },
  { url: "/contact", label: "Contact" },
  { url: "/become-a-das-centre", label: "Become a DAS Centre" },
];

/**
 * The secondary row under the header on every page but the homepage, the
 * current page underlined in green.
 *
 * It is the header's second row rather than a sibling, so it needs no
 * positioning of its own: the header is the fixed element and grows
 * --header-height by this row's 44px. It sits level with the bar (z-2) so the
 * full-screen menu passes beneath both rows. Only a bottom hairline: the top one
 * is the bar's own border-bottom.
 *
 * Over a hero (SiteWrapper's transparent-header mode, before the page scrolls
 * off it) the row stops painting along with the bar, whether or not the menu is
 * open.
 */
export default function PageNav() {
  const pathname = usePathname().replace(/\/+$/, "");

  return (
    <nav
      className="relative z-2 h-11 border-b border-das-hairline bg-white/80 backdrop-blur-[20px] transition-[background-color,border-color] duration-400 ease-[ease] group-data-[transparent-header=true]/site:group-data-[scrolled=false]/site:border-b-white/18 group-data-[transparent-header=true]/site:group-data-[scrolled=false]/site:bg-transparent group-data-[transparent-header=true]/site:group-data-[scrolled=false]/site:backdrop-filter-none"
      aria-label="Secondary"
    >
      <div className="shell h-full shell-s">
        {/* The labels never wrap or squash, so on a narrow screen the row scrolls sideways. */}
        <ul className="m-0 flex h-full list-none items-stretch overflow-x-auto overflow-y-hidden p-0 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {NAV_ITEMS.map((item) => {
            const active = item.url === pathname;

            return (
              <li key={item.url} className="m-0 shrink-0">
                <Link
                  href={item.url}
                  className={clsx(
                    "flex h-full items-center border-b-2 px-3.5 text-[13.5px] leading-[1.2] tracking-[-0.005em] whitespace-nowrap no-underline transition-[color,border-color] duration-180 ease-[ease]",
                    active
                      ? "border-b-das-green font-bold text-brand-blue group-data-[transparent-header=true]/site:group-data-[scrolled=false]/site:text-white"
                      : "border-b-transparent font-normal text-[#6b7280] hover:text-brand-blue group-data-[transparent-header=true]/site:group-data-[scrolled=false]/site:text-white/75 group-data-[transparent-header=true]/site:group-data-[scrolled=false]/site:hover:text-white",
                  )}
                  aria-current={active ? "page" : undefined}
                >
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
